import { useEffect, useState } from 'react'

import config from '../config'

import { TemperatureUnit, Unit, useLocale } from '@/context/localeContext'
import { WeatherCaptureBlock } from '@/modules/templates/types/blueprint'

type SimplifiedWeather = WeatherCaptureBlock['value']['weather']

export type WeatherInfo = {
  icon: string
  description: string
  temperature: number
  temperatureMin: number
  temperatureMax: number
  temperatureUnit: TemperatureUnit
  humidity: number
  sunrise: Date
  sunset: Date
  weather: string
  simplifiedWeather: SimplifiedWeather
  name: string
}

const getWeatherLatLngUrl = (lat: number, lng: number, unit: Unit) =>
  `https://api.openweathermap.org/data/2.5/weather?units=${unit}&lat=${lat}&lon=${lng}&appid=${config.openWeatherMapApiKey}`

const getIconUrl = (code) => `http://openweathermap.org/img/wn/${code}@2x.png`

export const useWeatherLatLng = (
  cancelFetch: boolean,
  lat: number,
  lng: number,
  onWeatherCallback?: (weather: WeatherInfo) => void
): [WeatherInfo, boolean] => {
  const { unit, temperatureUnit } = useLocale()

  const [weather, setWeather] = useState<WeatherInfo>()
  const [loadingWeather, setLoadingWeather] = useState(true)

  const haveLocation = lat && lng

  useEffect(() => {
    const getWeather = async () => {
      try {
        setLoadingWeather(true)
        const response = await fetch(getWeatherLatLngUrl(lat, lng, unit), {
          method: 'GET',
        })
        const weather = await response.json()

        const singleWeather = weather?.weather?.[0]

        if (singleWeather) {
          const weatherData: WeatherInfo = {
            description: singleWeather.description,
            icon: getIconUrl(singleWeather.icon),
            temperature: weather.main?.temp,
            temperatureMin: weather.main?.temp_min,
            temperatureMax: weather.main?.temp_max,
            temperatureUnit: temperatureUnit,
            humidity: weather.main?.humidity,
            sunrise: new Date() + weather.sys?.sunrise,
            sunset: new Date() + weather.sys?.sunset,
            weather: singleWeather.main,
            simplifiedWeather: getSimplifiedWeatherCode(singleWeather.main),
            name: weather.name,
          }

          setWeather(weatherData)
          onWeatherCallback?.(weatherData)
        }
      } catch {
        // do nothing
      } finally {
        setLoadingWeather(false)
      }
    }

    if (!cancelFetch && haveLocation) {
      getWeather()
    } else {
      setLoadingWeather(false)
    }
  }, [
    lat,
    lng,
    cancelFetch,
    haveLocation,
    unit,
    temperatureUnit,
    onWeatherCallback,
  ])

  return [weather, loadingWeather]
}

const getSimplifiedWeatherCode = (code: string): SimplifiedWeather => {
  switch (code?.toLowerCase()) {
    case 'thunderstorm':
    case 'drizzle':
    case 'rain':
    case 'snow':
      return 'raining'

    case 'clouds':
    case 'mist':
    case 'smoke':
    case 'haze':
    case 'dust':
    case 'fog':
    case 'sand':
    case 'ash':
    case 'squall':
    case 'tornado':
      return 'cloudy'

    case 'clear':
    default:
      return 'sunny'
  }
}
